import { FC } from 'react';
import { Typography, Stack, AccordionDetails, Button } from '@mui/material';
import { VyIcon, VyIconType } from '@vy/ui-design-system';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { Optional, ItemOf, raiseUnreachable, withDefault, now } from '@vy/utils';
import { formatDate } from '../../../../../../../../../../../../utils/date';
import { runMutation } from '../../../../../../../../../../../../utils/vy-mutation';
import { getMorningInvoiceUrl } from '../../../../../../../../../../../../services/integration-invoice/morning-integration-invoice-link';
import { useQueryClient } from 'react-query';
import { GetLightInvoicesResponse, GetExportedIntegrationsResponse } from '@vy/api-definition';
import { SYNC_JOB_STATUSES, SyncJobStatus } from '@vy/integrations-types';
import { useMorningExportInvoices } from '../../../../../../../../../../../../hooks/requests/integrations';
import { queryKeys } from '../../../../../../../../../../../../services/query-key/query-keys';

type MorningIntegrationStatusAccordionDetailsProps = {
  morningIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const MorningIntegrationStatusAccordionDetails: FC<MorningIntegrationStatusAccordionDetailsProps> = ({
  morningIntegration,
  invoice,
}) => {
  const exportToMorningMutation = useMorningExportInvoices();
  const queryClient = useQueryClient();

  const isApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isSyncable = isInvoiceSyncToIntegrationAllowed(invoice);
  const isSynced = morningIntegration?.status === SYNC_JOB_STATUSES.COMPLETED;
  const status = withDefault(morningIntegration?.status, SYNC_JOB_STATUSES.PENDING);
  const label = getLabel(status);
  const icon = getIcon(status);

  const getStatusText = () => {
    if (isSynced) {
      if (morningIntegration?.updatedAt) {
        return formatDate(morningIntegration.updatedAt);
      }
      return formatDate(now());
    }
    if (!isApproved) {
      return 'Approve invoice to enable syncing';
    }
    if (!isSyncable) {
      return 'Child invoices sync through the parent customer';
    }
    if (morningIntegration?.updatedAt) {
      return formatDate(morningIntegration.updatedAt);
    }
    return formatDate(now());
  };

  const handleSyncToMorning = async () => {
    await runMutation(
      exportToMorningMutation,
      { invoiceIds: [invoice.id] },
      {
        pending: 'Exporting to Morning...',
        success: 'Exported to Morning',
        error: 'Failed to export to Morning',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.invoice(invoice.id)),
            queryClient.invalidateQueries(queryKeys.exportedInvoiceIntegration(invoice.id)),
          ]);
        },
      },
    );
  };

  const handleOpenOnMorning = () => {
    const url = getMorningInvoiceUrl();

    window.open(url, '_blank');
  };

  return (
    <AccordionDetails sx={{ p: 0 }}>
      <Stack
        sx={{
          gap: 2,
          mb: 1.5,
          mt: 1.5
        }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: "74px",
            flexShrink: 0,
            justifyContent: "space-between"
          }}>
          <Stack
            direction="column"
            sx={{
              alignItems: "flex-start",
              gap: "4px"
            }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1
              }}>
              <VyIcon icon={icon} strokeOpacity={0} />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px"
                }}>
                {label}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "20px",
                color: "#7E8796"
              }}>
              {getStatusText()}
            </Typography>
          </Stack>

          {!isSynced && (
            <Button
              variant="text"
              size="medium"
              startIcon={<VyIcon icon="Synchronize" transform="scale(0.7)" />}
              onClick={handleSyncToMorning}
              disabled={!isSyncable}
            >
              Sync
            </Button>
          )}
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<VyIcon icon="OpenNew" />}
          onClick={handleOpenOnMorning}
          disabled={!isSynced}
        >
          Open on Morning
        </Button>
      </Stack>
    </AccordionDetails>
  );
};

function getLabel(status: SyncJobStatus): string {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'Morning not synced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'Sync failed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'Synced to Morning';
    default:
      return raiseUnreachable(status);
  }
}

function getIcon(status: SyncJobStatus): VyIconType {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'MorningNotSynced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'MorningSyncFailed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'MorningSynced';
    default:
      return raiseUnreachable(status);
  }
}
